'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import * as React from 'react';
import { useAuth } from '../auth';
import {
  useCheckChatAccessQuery,
  useCheckRecentPaymentQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
} from '../hooks/use-gateway-queries';
import { useElectron } from '../services/electron/electron-provider';
import { logError } from '../utils/safe-logger';
import { toast } from '../utils/toast';
import { Button } from './button';
import { EmbeddedPricingTable } from './embedded-pricing-table';
import { InfoSection } from './info-section';
import { PaymentPrompt } from './payment-prompt';
import { SubscriptionDetailsCard } from './subscription-details-card';

const logger = createLogger('client:component:billing-panel');

/**
 * BillingPanel component
 *
 * Displays billing management UI:
 * - For users without subscription: Embedded Stripe pricing table
 * - For users with subscription: Button to open Stripe customer portal
 *
 * The pricing table is now embedded directly in the app for a seamless experience.
 */
const BillingPanel: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const electron = useElectron();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showPricingTable, setShowPricingTable] = React.useState(false);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = React.useState<
    string | null
  >(null);
  const [checkPaymentStatus, setCheckPaymentStatus] = React.useState(false);
  const [isPollingBackground, setIsPollingBackground] = React.useState(false);

  // Check if user has entitlements
  const {
    data: chatAccessData,
    isLoading: isChatAccessLoading,
    refetch: refetchChatAccess,
    isFetching: isChatAccessFetching,
  } = useCheckChatAccessQuery(isAuthenticated && !!user?.organizationId);

  // Query for checking recent payment status
  const { data: paymentData, isLoading: isCheckingPayment } =
    useCheckRecentPaymentQuery(checkPaymentStatus);

  // Create checkout session mutation for getting client secret
  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation();

  // Create portal session mutation for opening billing portal (web)
  const createPortalSessionMutation = useCreatePortalSessionMutation();

  // Listen for extension success notifications (EXTENSION REQUIRED)
  React.useEffect(() => {
    const handleExtensionSuccess = (event: MessageEvent) => {
      if (event.source !== window) return;

      const message = event.data;
      if (message.type === 'STRIPE_CHECKOUT_SUCCESS') {
        logger.info('🎉 [BILLING-PANEL] Payment success from extension!', {
          sessionId: message.sessionId,
        });

        // Show success toast
        toast.success(
          'Payment Successful! 💳',
          "Your subscription is being activated. You'll be notified when it's ready (usually < 1 minute)."
        );

        // The EmbeddedPricingTable will handle background polling
      }
    };

    window.addEventListener('message', handleExtensionSuccess);

    return () => {
      window.removeEventListener('message', handleExtensionSuccess);
    };
  }, []);

  // Handle payment status check result
  const handleSubscribe = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      logger.info('🔄 [BILLING-PANEL] Getting customer session for embedded pricing table');

      // Call the API to get the customer session client secret
      const response = await createCheckoutSessionMutation.mutateAsync({});

      if (response.status === 200 && response.body.customerSessionClientSecret) {
        logger.info(
          '✅ [BILLING-PANEL] Got customer session client secret, showing embedded pricing table'
        );
        setCustomerSessionClientSecret(response.body.customerSessionClientSecret);
        setShowPricingTable(true);
      } else {
        logger.error('❌ [BILLING-PANEL] Failed to get client secret:', response);
        setError('Failed to load pricing table. Please try again later.');
      }
    } catch (error) {
      logger.error('❌ [BILLING-PANEL] Error loading pricing table:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      logError('BILLING-PANEL Error loading pricing table', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle payment status check result
  React.useEffect(() => {
    if (!checkPaymentStatus || isCheckingPayment) return;

    if (paymentData) {
      logger.info('💳 [BILLING-PANEL] Payment status check result:', paymentData);

      if (paymentData.body.paymentCompleted) {
        logger.info(
          '✅ [BILLING-PANEL] Payment confirmed via Stripe API! Starting background polling'
        );

        // Show success toast
        toast.success(
          'Payment Confirmed! ✅',
          'Starting background check for subscription activation...'
        );

        // Start background polling (non-blocking)
        setIsPollingBackground(true);
      } else {
        logger.info('ℹ️ [BILLING-PANEL] No payment detected - checkout may have been cancelled');
      }

      // Reset the check flag
      setCheckPaymentStatus(false);
    }
  }, [paymentData, isCheckingPayment, checkPaymentStatus]);

  // Background polling for entitlement after payment - non-blocking!
  React.useEffect(() => {
    if (!isPollingBackground) return;

    logger.info('🔄 [BILLING-PANEL] Starting background entitlement polling...');

    let pollCount = 0;
    const maxPolls = 45; // 45 attempts = 90 seconds
    const intervalMs = 2000; // Check every 2 seconds

    const pollInterval = setInterval(async () => {
      pollCount++;
      logger.info(`🔄 [BILLING-PANEL] Background poll ${pollCount}/${maxPolls}`);

      try {
        const result = await refetchChatAccess();
        const hasAccess = result?.data?.body?.hasAccess;

        if (hasAccess) {
          logger.info('✅ [BILLING-PANEL] Entitlement granted via background poll!');
          clearInterval(pollInterval);
          setIsPollingBackground(false);

          // Show success toast
          toast.success(
            'Subscription Active! 🎉',
            'Your subscription is now active. You can start using chat!'
          );

          // Hide pricing table if showing
          setShowPricingTable(false);
          setCustomerSessionClientSecret(null);
          return;
        }
      } catch (error) {
        logger.error('❌ [BILLING-PANEL] Background poll error:', error);
      }

      // Stop after max attempts
      if (pollCount >= maxPolls) {
        logger.warn('⚠️ [BILLING-PANEL] Background polling timeout after 90 seconds');
        clearInterval(pollInterval);
        setIsPollingBackground(false);

        toast.info(
          'Subscription Activating',
          'Your payment was successful. Activation is taking longer than usual - please check back in a few minutes or refresh.'
        );
      }
    }, intervalMs);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isPollingBackground, refetchChatAccess]);

  // Handle successful subscription
  const handleSubscriptionSuccess = () => {
    logger.info('🎉 [BILLING-PANEL] Subscription completed successfully');
    setShowPricingTable(false);
    setCustomerSessionClientSecret(null);
    // The EmbeddedPricingTable component already refreshes auth
    // The UI will automatically update when chat access query refetches
  };

  // Handle pricing table errors
  const handlePricingTableError = (error: string) => {
    logger.error('❌ [BILLING-PANEL] Pricing table error:', error);
    setError(error);
    setShowPricingTable(false);
    setCustomerSessionClientSecret(null);
  };

  // Handle manual subscription refresh
  const handleRefreshSubscription = async () => {
    logger.info('🔄 [BILLING-PANEL] Manual subscription refresh requested');
    try {
      await refetchChatAccess();
      logger.info('✅ [BILLING-PANEL] Subscription data refreshed');
    } catch (error) {
      logger.error('❌ [BILLING-PANEL] Failed to refresh subscription:', error);
      logError('BILLING-PANEL Failed to refresh subscription', error);
    }
  };

  // Handle opening the customer portal
  const handleOpenPortal = async () => {
    if (!user) {
      setError('User not authenticated');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      logger.info('🔄 [BILLING-PANEL] Opening customer portal');

      // Check if running in Electron or web
      if (electron.isElectronAvailable) {
        // Desktop: Use Electron API to open portal in native window
        logger.info('🔄 [BILLING-PANEL] Using Electron API for desktop');
        const result = await electron.billing.openCustomerPortal();
        logger.info('🔄 [BILLING-PANEL] Result:', result);

        if (!result) {
          logger.error('❌ [BILLING-PANEL] Received undefined result from openCustomerPortal');
          setError('Failed to open customer portal - no response received');
        } else if (result.success) {
          logger.info('✅ [BILLING-PANEL] Successfully opened customer portal');
        } else {
          logger.error('❌ [BILLING-PANEL] Failed to open customer portal:', result.error);
          setError(result.error || 'Failed to open customer portal');
        }
      } else {
        // Web: Use HTTP API to get portal URL and open in new tab
        logger.info('🔄 [BILLING-PANEL] Using HTTP API for web');
        const response = await createPortalSessionMutation.mutateAsync({});

        if (response.status === 200 && response.body.url) {
          logger.info('✅ [BILLING-PANEL] Got portal URL, opening in new tab');
          // Open in new tab
          window.open(response.body.url, '_blank', 'noopener,noreferrer');
        } else {
          logger.error('❌ [BILLING-PANEL] Failed to get portal URL:', response);
          setError('Failed to open billing portal. Please try again later.');
        }
      }
    } catch (error) {
      logger.error('❌ [BILLING-PANEL] Error opening customer portal:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      logError('BILLING-PANEL Error opening customer portal', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (isLoading || isChatAccessLoading) {
    return (
      <div className="flex items-start h-full w-full p-4 sm:p-6 md:p-8">
        <div className="text-center w-full max-w-md mx-auto mt-8 sm:mt-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="break-words">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="flex flex-col items-start h-full w-full p-4 sm:p-6 md:p-8">
        <InfoSection className="text-center w-full max-w-md mx-auto mt-8 sm:mt-12">
          <h3 className="text-lg font-semibold mb-2 break-words">An error occurred</h3>
          <p className="text-secondary-enhanced mb-4 break-words">{error}</p>
          <Button
            variant="primary"
            size="md"
            onClick={() => setError(null)}
            className="w-full sm:w-auto"
          >
            Try Again
          </Button>
        </InfoSection>
      </div>
    );
  }

  // Check if user has entitlements
  const hasSubscriptionAccess = chatAccessData?.body?.hasAccess;
  const subscriptionData = chatAccessData?.body?.subscription;
  // Manual (invoice-billed) orgs have no Stripe customer — hide the Stripe billing portal.
  const isManualBilling = subscriptionData?.billingType === 'manual';

  // If user doesn't have full access yet
  if (!hasSubscriptionAccess) {
    // If showing pricing table, render it
    if (showPricingTable && customerSessionClientSecret) {
      return (
        <div className="h-full w-full overflow-hidden">
          <EmbeddedPricingTable
            customerSessionClientSecret={customerSessionClientSecret}
            onSuccess={handleSubscriptionSuccess}
            onError={handlePricingTableError}
          />
        </div>
      );
    }

    // If subscription is processing (just paid, waiting for WorkOS sync), show status card
    if (subscriptionData && subscriptionData.status === 'processing') {
      return (
        <div className="flex flex-col items-start h-full w-full p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <InfoSection variant="primary">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2 break-words">Payment Successful!</h2>
                <p className="text-secondary-enhanced break-words">
                  Your payment was confirmed. We're activating your subscription now (usually {'<'}{' '}
                  1 minute).
                </p>
              </div>
            </InfoSection>

            <SubscriptionDetailsCard
              subscription={subscriptionData}
              onRefresh={handleRefreshSubscription}
              isRefreshing={isChatAccessFetching}
            />
          </div>
        </div>
      );
    }

    // If subscription status is "pending" (has sub, needs BAA), show helpful message
    if (subscriptionData && subscriptionData.status === 'pending') {
      return (
        <div className="flex flex-col items-start h-full w-full p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <InfoSection variant="primary">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2 break-words">Almost There!</h2>
                <p className="text-secondary-enhanced break-words">
                  Your subscription is active. Please sign the BAA agreement to start using chat.
                </p>
              </div>
            </InfoSection>

            <SubscriptionDetailsCard
              subscription={subscriptionData}
              onRefresh={handleRefreshSubscription}
              isRefreshing={isChatAccessFetching}
            />
          </div>
        </div>
      );
    }

    // Manual/invoice orgs never use Stripe. Reaching here means the account is paused (suspended)
    // — show a contact-rep notice, NOT a Stripe payment prompt (which would spin up an orphan
    // Stripe customer for an org that's meant to have none).
    if (isManualBilling) {
      return (
        <div className="flex flex-col items-start h-full w-full p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="w-full max-w-3xl mx-auto space-y-6">
            <InfoSection variant="primary">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2 break-words">Account paused</h2>
                <p className="text-secondary-enhanced break-words">
                  This invoiced account is currently paused. Please contact your Enter representative
                  to restore access.
                </p>
              </div>
            </InfoSection>
          </div>
        </div>
      );
    }

    // Otherwise show payment prompt
    return <PaymentPrompt onAddPayment={handleSubscribe} isLoading={isLoading} />;
  }

  // If user has entitlements, show detailed subscription info and billing portal button
  return (
    <div className="flex flex-col items-start h-full w-full p-1 sm:p-4 md:p-8 overflow-y-auto">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        <InfoSection>
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2 break-words">Manage Your Subscription</h2>
            <p className="text-secondary-enhanced break-words">
              View your subscription details and manage your billing settings.
            </p>
          </div>
        </InfoSection>

        {/* Show detailed subscription info if available */}
        {subscriptionData && (
          <SubscriptionDetailsCard
            subscription={subscriptionData}
            onRefresh={handleRefreshSubscription}
            isRefreshing={isChatAccessFetching}
          />
        )}

        {isManualBilling ? (
          /* Invoice-billed account — no Stripe portal. */
          <p className="text-center text-sm text-muted-foreground pt-4">
            This is an invoiced account managed by Enter. For seat changes or billing questions,
            contact your Enter representative.
          </p>
        ) : (
          <>
            {/* Billing Portal Button */}
            <div className="pt-4 flex justify-center">
              <Button
                variant="primary"
                size="lg"
                onClick={handleOpenPortal}
                disabled={isLoading}
                className="w-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" x2="21" y1="14" y2="3" />
                </svg>
                Open Billing Portal
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Use the billing portal to update payment methods, view invoices, and manage your
              subscription plan.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

BillingPanel.displayName = 'BillingPanel';

export { BillingPanel };
